import React, { createContext, useContext, useState, useEffect } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { User } from "../types";
import { AppAbility, buildAbilityFor } from "../lib/ability";
import { createMongoAbility } from "@casl/ability";
import { AbilityContext } from "../context/casl/AbilityContext";
import { AUTH_UNAUTHORIZED_EVENT, api } from "../utils/api";


interface AuthContextType {
  currentUser: User | null;
  token: string | null;
  login: (email: string, password: string, captchaToken: string) => Promise<{ success: boolean; user: User | null; permissions: string[] }>;
  pinLogin: (deviceId: string, pin: string) => Promise<{ success: boolean; user: User | null; permissions: string[]; code?: string; message?: string }>;
  magicLogin: (token: string, user: User, permissions: string[]) => void;
  updateCurrentUser: (updates: Partial<User>) => void;
  logout: () => void;
  handleTokenExpiration: () => void;
  isAuthenticated: boolean;
  loading: boolean;
  updatePassword: (newPassword: string) => Promise<boolean>;
  signup: (
    email: string,
    password: string,
    mobile: string,
    jobRole: string,
    department: string,
    name: string
  ) => Promise<boolean>;
  permissions: string[];
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);
const emptyAbility = createMongoAbility<AppAbility>();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

function normalizeUser(user: any) {
  if (user && user.id && !user.id) {
    user.id = user.id;
    delete user.id;
  }
  return user;
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [permissions, setPermissions] = useState<string[]>([]);
  const [ability, setAbility] = useState<AppAbility>(emptyAbility);
  const navigate = useNavigate();

  useEffect(() => {
    setAbility(buildAbilityFor(permissions));
  }, [permissions]);

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const storedUser = localStorage.getItem("user");
        const storedToken = localStorage.getItem("token");
        const storedPermissions = localStorage.getItem("permissions");

        if (storedUser && storedToken) {
          // Set state immediately from localStorage for a snappy user experience.
          setCurrentUser(JSON.parse(storedUser));
          setToken(storedToken);
          setPermissions(JSON.parse(storedPermissions || '[]'));
          setIsAuthenticated(true);

          // Validate the token and get fresh user data
          const data = await api.get("/api/auth/me", { skipAuthHandler: true });

          if (data && data.success) {
            const normalizedUser = normalizeUser(data.user);
            setCurrentUser(normalizedUser);
            setPermissions(data.permissions);
            setToken(storedToken);
            setIsAuthenticated(true);
          } else {
            // Token is invalid or validation failed, clean up
            handleTokenExpiration();
          }
        }
      } catch (error) {
        console.error("Auth initialization error:", error);
        handleTokenExpiration();
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);

  const clearAuthData = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    localStorage.removeItem("permissions");
    setCurrentUser(null);
    setToken(null);
    setPermissions([]);
    setIsAuthenticated(false);
  };

  const handleTokenExpiration = () => {
    console.log('[AuthContext] Token expiration triggered:', {
      previousState: {
        hadToken: !!token,
        tokenValue: token?.substring(0, 10) + "...",
        wasAuthenticated: isAuthenticated,
        hadUser: !!currentUser?.id,
        userId: currentUser?.id
      },
      localStorage: {
        hadToken: !!localStorage.getItem("token"),
        tokenValue: localStorage.getItem("token")?.substring(0, 10) + "...",
        hadUser: !!localStorage.getItem("user")
      }
    });

    // Clear state and localStorage
    // localStorage.removeItem("user");
    // localStorage.removeItem("token");
    // setCurrentUser(null);
    // setToken(null);
    // setIsAuthenticated(false);
    clearAuthData();

    console.log('[AuthContext] State after cleanup:', {
      currentState: {
        hasToken: !!token,
        isAuthenticated,
        hasUser: !!currentUser?.id
      },
      localStorage: {
        hasToken: !!localStorage.getItem("token"),
        hasUser: !!localStorage.getItem("user")
      }
    });

    toast.error("Your session has expired. Please log in again.");
    navigate("/login");
  };

  // Listen for unauthorized access events from the API helper
  useEffect(() => {
    const handleUnauthorized = () => {
      handleTokenExpiration();
    };

    window.addEventListener(AUTH_UNAUTHORIZED_EVENT, handleUnauthorized);

    return () => {
      window.removeEventListener(AUTH_UNAUTHORIZED_EVENT, handleUnauthorized);
    };
  }, [token, isAuthenticated, currentUser]); // Re-bind when auth state changes to ensure closure captures latest state if needed, though handleTokenExpiration reads mostly from refs/closure. 
  // Actually, handleTokenExpiration uses navigate and clearAuthData. clearAuthData uses setState.
  // Ideally we should just rely on the re-render cycle. using handleTokenExpiration as dep is safer but leads to churn.
  // Given handleTokenExpiration is NOT memoized, we MUST put it in deps or allow the churn.


  const login = async (email: string, password: string, captchaToken: string): Promise<{ success: boolean; user: User | null; permissions: string[] }> => {
    try {
      const data = await api.post("/api/auth/login", { email, password, captchaToken }, { skipAuthHandler: true });

      if (!data || !data.success || !data.token || !data.user || !data.permissions) {
        console.log("❌ Login failed:", data.message);
        toast.error(data.message || "Login failed");
        return { success: false, user: null, permissions: [] };
      }

      const { token: newToken, user, permissions: newPermissions } = data;
      const normalizedUser = normalizeUser(user);

      // Set state first
      setToken(newToken);
      setCurrentUser(normalizedUser);
      setPermissions(newPermissions);
      setIsAuthenticated(true);

      // Then localStorage
      localStorage.setItem("token", newToken);
      localStorage.setItem("user", JSON.stringify(normalizedUser));
      localStorage.setItem("permissions", JSON.stringify(newPermissions));

      // Verify everything is set before proceeding
      const storedToken = localStorage.getItem("token");
      const storedUser = localStorage.getItem("user");

      // Wait for state to be updated
      await new Promise(resolve => setTimeout(resolve, 100));

      // Final verification before proceeding
      if (storedToken && storedUser) {
        console.log("🚀 Auth state ready");
        toast.success("Logged in successfully");
        return { success: true, user: normalizedUser, permissions: newPermissions };
      } else {
        console.log("❌ Auth state not properly set");
        toast.error("Login failed - please try again");
        return { success: false, user: null, permissions: [] };
      }
    } catch (err) {
      console.error("🚨 Login error:", err);
      toast.error("Login error");
      return { success: false, user: null, permissions: [] };
    }
  };

  const logout = () => {
    clearAuthData();
    toast.success("You have been logged out");
    navigate("/login");
  };

  // PIN login: deviceId (stored on this device) + PIN, verified server-side.
  // Error codes from the API (PIN_LOCKED, PIN_DEVICE_REVOKED) are passed
  // through so the login page can react (e.g. fall back to password form).
  const pinLogin = async (deviceId: string, pin: string): Promise<{ success: boolean; user: User | null; permissions: string[]; code?: string; message?: string }> => {
    try {
      const data = await api.post("/api/auth/pin/login", { deviceId, pin }, { skipAuthHandler: true, skipErrorToast: true });

      if (!data || !data.success || !data.token || !data.user || !data.permissions) {
        return { success: false, user: null, permissions: [], message: data?.message || "Login failed" };
      }

      const { token: newToken, user, permissions: newPermissions } = data;
      const normalizedUser = normalizeUser(user);

      setToken(newToken);
      setCurrentUser(normalizedUser);
      setPermissions(newPermissions);
      setIsAuthenticated(true);

      localStorage.setItem("token", newToken);
      localStorage.setItem("user", JSON.stringify(normalizedUser));
      localStorage.setItem("permissions", JSON.stringify(newPermissions));

      // Wait for state to be updated (same as password login)
      await new Promise(resolve => setTimeout(resolve, 100));

      toast.success("Logged in successfully");
      return { success: true, user: normalizedUser, permissions: newPermissions };
    } catch (err: any) {
      return {
        success: false,
        user: null,
        permissions: [],
        code: err?.data?.code,
        message: err?.data?.message || err?.message || "Login failed",
      };
    }
  };

  const updatePassword = async (newPassword: string): Promise<boolean> => {
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      toast.success("Password updated successfully");
      return true;
    } catch (error) {
      toast.error("Failed to update password");
      return false;
    }
  };

  const magicLogin = (newToken: string, user: User, newPermissions: string[]) => {
    const normalizedUser = normalizeUser(user);
    setToken(newToken);
    setCurrentUser(normalizedUser);
    setPermissions(newPermissions);
    setIsAuthenticated(true);
    localStorage.setItem("token", newToken);
    localStorage.setItem("user", JSON.stringify(normalizedUser));
    localStorage.setItem("permissions", JSON.stringify(newPermissions));
  };

  // Keep the signed-in user's own record in sync when their account is edited
  // elsewhere (e.g. an admin changing their own email in user management).
  const updateCurrentUser = (updates: Partial<User>) => {
    setCurrentUser(prev => {
      if (!prev) return prev;
      const next = { ...prev, ...updates };
      localStorage.setItem("user", JSON.stringify(next));
      return next;
    });
  };

  const signup = async (
    email: string,
    password: string,
    mobile: string,
    jobRole: string,
    department: string,
    name: string
  ): Promise<boolean> => {
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      toast.success("Signup successful");
      return true;
    } catch (error) {
      toast.error("Signup failed");
      return false;
    }
  };

  const value = {
    currentUser,
    token,
    permissions,
    login,
    pinLogin,
    magicLogin,
    updateCurrentUser,
    logout,
    handleTokenExpiration,
    isAuthenticated,
    loading,
    updatePassword,
    signup,
  };

  return (
    <AuthContext.Provider value={value}>
      <AbilityContext.Provider value={ability}>
        {!loading && children}
      </AbilityContext.Provider>
    </AuthContext.Provider>
  );
};
